/**
 * The student's own record across every paper they have sat.
 *
 * `GET /api/attempts/my-progress` is scoped to the signed-in student on the
 * server, so there is no id to pass and nothing here can name another student:
 * the only cohort figure is an anonymous mean the API withholds below
 * `COHORT_MEAN_MIN` sittings.
 *
 * The fetch mirrors `./result.ts` — a plain call, validated against the shared
 * schema so a contract drift fails loudly here instead of rendering a chart of
 * `undefined`. There is deliberately no sample fallback: a student's own record
 * is the last place to invent numbers.
 *
 * Everything below the fetch is pure view-model work — scales, retake flags,
 * captions. It lives here rather than in the page so the arithmetic that
 * decides what a student is told about their progress can be read (and later
 * tested) on its own.
 */
import {
  MyProgressResponse,
  TOPIC_ADVICE_MIN,
  type MyAttemptRow,
  type MyProgressResponse as MyProgress,
  type MyTopicRow,
} from "@exam/shared";
import { api } from "@/lib/api";

export async function fetchMyProgress(): Promise<MyProgress> {
  const raw = await api.get<unknown>("/api/attempts/my-progress");
  return MyProgressResponse.parse(raw);
}

/* ------------------------------------------------------------------ */
/* Score-over-time chart                                               */
/* ------------------------------------------------------------------ */

/** Fixed 0–100 axis: percent is already comparable, and a scale that hugs the
 *  data would make a 4-point wobble look like a collapse. */
const Y_MIN = 0;
const Y_MAX = 100;

export const CHART = {
  width: 720,
  height: 240,
  padTop: 14,
  padRight: 14,
  padBottom: 46,
  padLeft: 36,
} as const;

export interface ProgressPoint {
  attempt: MyAttemptRow;
  x: number;
  y: number;
  /** Pixel y of the class mean for the same paper, when the API released one. */
  cohortY: number | null;
  /** A second (or later) sitting of the SAME paper — not a new data point in a
   *  trend, and drawn with its own marker so it can never be read as one. */
  isRetake: boolean;
  /** True when the previous point is the same paper, so the connector between
   *  them joins two sittings of one exam rather than two exams over time. */
  sameExamAsPrevious: boolean;
}

export interface ProgressChart {
  points: ProgressPoint[];
  /** Horizontal gridlines, top to bottom, each already positioned. */
  grid: Array<{ y: number; label: string }>;
  /** `M x y L x y …` for the solid connectors only — dashed retake links are
   *  drawn per-segment so they can carry their own title. */
  plotLeft: number;
  plotRight: number;
  plotTop: number;
  plotBottom: number;
}

function yFor(percent: number): number {
  const clamped = Math.max(Y_MIN, Math.min(Y_MAX, percent));
  const inner = CHART.height - CHART.padTop - CHART.padBottom;
  return CHART.padTop + inner * (1 - (clamped - Y_MIN) / (Y_MAX - Y_MIN));
}

/**
 * Build the chart geometry.
 *
 * Points are spaced evenly in sitting order rather than by true elapsed time:
 * with three or four sittings a real time axis collapses two papers sat in the
 * same week into a single column and stretches a summer gap across half the
 * canvas, which reads as a dramatic slump that never happened. Every point
 * still carries its submission date in the axis label and in its tooltip, so
 * the timeline stays honest — only the spacing is regularised.
 */
export function buildChart(attempts: MyAttemptRow[]): ProgressChart {
  const ordered = [...attempts].sort((a, b) => a.submitted_at - b.submitted_at);
  const plotLeft = CHART.padLeft;
  const plotRight = CHART.width - CHART.padRight;
  const plotTop = CHART.padTop;
  const plotBottom = CHART.height - CHART.padBottom;

  const span = plotRight - plotLeft;
  const points: ProgressPoint[] = ordered.map((attempt, i) => {
    // A single sitting sits in the middle; with more, the first and last hug
    // the axis ends so the whole width is used.
    const t = ordered.length === 1 ? 0.5 : i / (ordered.length - 1);
    const previous = i > 0 ? (ordered[i - 1] ?? null) : null;
    return {
      attempt,
      x: plotLeft + span * t,
      y: yFor(attempt.percent),
      cohortY:
        attempt.cohort_mean_percent === null
          ? null
          : yFor(attempt.cohort_mean_percent),
      isRetake: attempt.attempt_no > 1,
      sameExamAsPrevious:
        previous !== null && previous.exam_id === attempt.exam_id,
    };
  });

  return {
    points,
    grid: [0, 25, 50, 75, 100].map((v) => ({ y: yFor(v), label: `${v}%` })),
    plotLeft,
    plotRight,
    plotTop,
    plotBottom,
  };
}

/**
 * What we are willing to claim from this many sittings.
 *
 * One point is a measurement, two are a comparison, and only three or more
 * start to look like a direction — and even then the wording stays hedged,
 * because papers differ in difficulty and this page must not talk a student
 * into believing a story the data cannot support.
 */
export function trendCaption(attempts: MyAttemptRow[]): string {
  const n = attempts.length;
  if (n === 0) return "";
  if (n === 1) {
    return "One sitting so far — there is nothing yet to compare it against. The line starts to mean something from your next paper.";
  }
  if (n === 2) {
    return "Two sittings: that is a difference, not yet a trend. Papers vary in difficulty, so give it another paper or two before reading anything into it.";
  }

  const distinct = new Set(attempts.map((a) => a.exam_id)).size;
  if (distinct < 3) {
    return `${n} sittings, but only ${distinct} different papers — mostly retakes, so this shows how a paper went the second time rather than a direction over time.`;
  }
  return `${n} sittings across ${distinct} papers. Look at the shape rather than any single point: one hard paper is a hard paper, not a slump.`;
}

/* ------------------------------------------------------------------ */
/* Topics                                                              */
/* ------------------------------------------------------------------ */

/** A topic answered fewer than `TOPIC_ADVICE_MIN` times: shown for
 *  completeness, but explicitly marked as too thin to revise from, because a
 *  "0% accuracy" built on one question is how someone panics over bad luck. */
export function isTooThinToAct(topic: MyTopicRow): boolean {
  return topic.answered < TOPIC_ADVICE_MIN;
}

/** Weakest first, but every topic with enough evidence ranked above every
 *  topic without it — otherwise a one-question 0% tops the list. */
export function sortTopics(topics: MyTopicRow[]): MyTopicRow[] {
  return [...topics].sort((a, b) => {
    const thin = Number(isTooThinToAct(a)) - Number(isTooThinToAct(b));
    if (thin !== 0) return thin;
    if (a.accuracy !== b.accuracy) return a.accuracy - b.accuracy;
    return b.answered - a.answered;
  });
}

/* ------------------------------------------------------------------ */
/* Time management                                                     */
/* ------------------------------------------------------------------ */

export interface TimeUse {
  attempt: MyAttemptRow;
  /** 0..1+ of the allowance actually used, or null when the API had no
   *  recorded duration for the sitting. */
  ratio: number | null;
  note: string;
}

/**
 * `time_allowed_ms` already includes any extra time granted, so this compares
 * against what the student was actually given and never against the paper's
 * nominal duration — an accommodation must never surface here as an overrun.
 */
export function timeUse(attempt: MyAttemptRow): TimeUse {
  if (attempt.time_taken_ms === null || attempt.time_allowed_ms === 0) {
    return { attempt, ratio: null, note: "No timing recorded for this sitting." };
  }
  const ratio = attempt.time_taken_ms / attempt.time_allowed_ms;
  let note: string;
  if (ratio >= 0.97) {
    note = "You used essentially the whole allowance — worth practising pace.";
  } else if (ratio >= 0.8) {
    note = "You used most of the time, with a little in hand.";
  } else if (ratio >= 0.55) {
    note = "Comfortably inside the time.";
  } else {
    note =
      "You finished with a lot of time unused — there was room to check your work.";
  }
  return { attempt, ratio, note };
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

/** Null means "nothing yet", never 0% — a student who has sat nothing has not
 *  scored zero. */
export function percentText(value: number | null): string {
  return value === null ? "—" : `${Math.round(value)}%`;
}

export function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

export function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function formatDuration(ms: number): string {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
